// Chance (0-100) that an attack connects with a target, before the attack's
// own accuracy. FFT evasions are independent multiplicative checks — the
// attack lands only if it beats each applicable evade — so the hit chance is
// 100 × Π(1 − evadeᵢ/100). This mirrors the front/side/back logic that has
// long lived inline in inlineUnit.vue.
//
// Evade sources by physical direction:
//   front: class C-EV + shield + accessory (+ weapon, only with Parry)
//   side:  shield + accessory             (+ weapon, only with Parry)
//   back:  accessory only
// Magical evade (shield + accessory) applies from any direction.
// Guns and Jump can't be evaded — callers skip this for them.
//
// target: {
//   equipped: { Mainhand, Offhand, Head, Armor, Accessory },  // normalized items
//   cEv,        // class C-EV percent
//   reaction,   // ReactionSkill name (Parry enables weapon evade; Abandon
//               //   doubles every evade — it's always on, needs no trigger)
//   defending,  // optional: true while the unit has the 'Defending' status,
//               //   which doubles evade the same way Abandon does (stacks ×4)
// }
// Normalized items carry pevade (physical), mevade (magical), wevade (weapon).

// The evade percentages that apply, one per source (class, shield, accessory, weapon), before Abandon or
// Defending: the game rolls each separately (battleSim's evadeHit scales them per attack).
export function evadeSources(target, { type = 'physical', direction = 'front' } = {}) {
  const e = target.equipped;
  const evades = [];
  const mhShield = e.Mainhand.type === 'Shield';

  if (type === 'magical') {
    if (mhShield) evades.push(e.Mainhand.mevade);
    evades.push(e.Offhand.mevade, e.Accessory.mevade);
  } else if (direction === 'back') {
    evades.push(e.Accessory.pevade);
  } else {
    if (direction === 'front') evades.push(target.cEv);
    if (mhShield) evades.push(e.Mainhand.pevade);
    evades.push(e.Offhand.pevade, e.Accessory.pevade);
    if (target.reaction === 'Parry') evades.push(e.Mainhand.wevade, e.Offhand.wevade);
  }
  return evades.map((ev) => ev || 0);
}

export function hitChance(target, opts = {}) {
  const evades = evadeSources(target, opts);

  // Abandon (always-on reaction) and the 'Defending' status each double every
  // evade percentage; both at once quadruple it (BMG §1.4). Each factor is
  // (1 − k·Ev), clamped at 0 — these are independent dodge checks, so once any
  // single doubled evade reaches 100% the attack can never connect.
  const k = (target.reaction === 'Abandon' ? 2 : 1) * (target.defending ? 2 : 1);

  let hit = 100;
  for (const ev of evades) hit *= Math.max(0, 100 - k * (ev || 0)) / 100;
  return hit;
}
